/**
 * Panel ID Constants
 *
 * Centralized definition of all panel IDs used in the side navigation system.
 * Use these constants instead of hardcoded strings to ensure consistency
 * and enable IDE autocompletion.
 *
 * @see src/features/Share/config/menuConfig.js - Panel component mappings
 * @see src/features/Share/modules/general.modules.js - Panel state management
 */

export const PANEL_IDS = Object.freeze({
  // Search & Discovery
  GLOBAL_SEARCH: 'global-search',

  // Viewer Content
  PLANS_2D: 'plans-2d',
  SCANS: 'scans',
  MODELS: 'models',
  BOOKMARKS: 'bookmarks',

  // Tags System
  TAGS: 'tags',
  TAGS_EQUIPMENT: 'tags-equipment',
  TAGS_PIPELINE: 'tags-pipeline',
  TAGS_STRUCTURAL: 'tags-structural',
  TAGS_CONTAINER: 'tags-container',
  TAGS_SCAFFOLD: 'tags-scaffold',
  TAGS_GENERAL: 'tags-general',
  TAGS_PERMIT: 'tags-permit',
  TAGS_ISOLATION: 'tags-isolation',
  TAGS_ANOMALY: 'tags-anomaly',
  TAGS_OPERATION: 'tags-operation',

  // Documents & Attachments
  DOCUMENTS: 'documents',

  // Downloads
  SCAN_DOWNLOAD: 'scan-download',

  // Inspection & Condition
  CONDITION_MAP: 'condition-map',
  INSPECTION_INFO: 'inspection-info',         // Separate panel for inspection details
  AVA_INSPECTION_INFO: 'ava-inspection-info', // Separate panel for AVA inspection details

  // Settings
  DISPLAY_SETTINGS: 'display-settings',

  // Management
  REGISTERS: 'registers',
  WORKSPACES: 'workspaces',
});

/**
 * Tag type suffix used to build tag-type panel IDs
 * Maps from tag type names to their panel ID suffixes
 */
export const TAG_TYPE_SUFFIXES = Object.freeze({
  EQUIPMENT: 'equipment',
  PIPELINE: 'pipeline',
  STRUCTURAL: 'structural',
  CONTAINER: 'container',
  SCAFFOLD: 'scaffold',
  GENERAL: 'general',
  PERMIT: 'permit',
  ISOLATION: 'isolation',
  ANOMALY: 'anomaly',
  OPERATION: 'operation',
});

/**
 * Prefix used for tag-type panel IDs
 */
export const TAGS_PANEL_PREFIX = 'tags-';

/**
 * Helper to check if a panel ID is a tag-type panel (e.g., 'tags-equipment')
 * @param {string} panelId
 * @returns {boolean}
 */
export function isTagTypePanel(panelId) {
  return panelId?.startsWith(TAGS_PANEL_PREFIX) && panelId !== PANEL_IDS.TAGS;
}

/**
 * Helper to check if a panel is hierarchical (has multi-level navigation)
 * @param {string} panelId
 * @returns {boolean}
 */
export function isHierarchicalPanel(panelId) {
  // Note: CONDITION_MAP removed - now uses side-by-side panels (inspection-info, ava-inspection-info)
  return panelId === PANEL_IDS.TAGS;
}

/**
 * Get the tag type from a tag-type panel ID
 * @param {string} panelId - e.g., 'tags-equipment'
 * @returns {string|null} - e.g., 'Equipment' or null if not a tag panel
 */
export function getTagTypeFromPanelId(panelId) {
  if (!isTagTypePanel(panelId)) return null;
  const type = panelId.replace(TAGS_PANEL_PREFIX, '');
  return type.charAt(0).toUpperCase() + type.slice(1);
}

/**
 * Build a tag-type panel ID from a tag type name
 * @param {string} typeName - e.g., 'Equipment', 'Pipeline'
 * @returns {string} - e.g., 'tags-equipment'
 */
export function buildTagTypePanelId(typeName) {
  return `${TAGS_PANEL_PREFIX}${typeName.toLowerCase()}`;
}

/**
 * Check if a panel ID matches a specific panel constant
 * Useful for comparisons when you want IDE support
 * @param {string} panelId - The panel ID to check
 * @param {string} expectedPanel - The expected panel from PANEL_IDS
 * @returns {boolean}
 */
export function isPanelId(panelId, expectedPanel) {
  return panelId === expectedPanel;
}

export default {
  PANEL_IDS,
  TAG_TYPE_SUFFIXES,
  TAGS_PANEL_PREFIX,
  isTagTypePanel,
  isHierarchicalPanel,
  getTagTypeFromPanelId,
  buildTagTypePanelId,
  isPanelId,
};
